import React, { useState, useMemo } from "react";
import {
  DragDropContext,
  Droppable,
  Draggable,
  DropResult,
} from "react-beautiful-dnd";
import { FaSearch } from "react-icons/fa";
import { X } from "lucide-react";
import styles from "./DragAndDrop.module.scss";
import Drage from "../../icons/Draganddrop.svg?react";
import PolicyIcon from "../../icons/theme.svg?react";
import CustomIcon from "../../icons/auditcustomTheme.svg?react";

import { getIconUrl } from "../../iconUtils";

interface FieldMapping {
  idKey: string;
  nameKey: string;
  codeKey?: string;
  imageKey?: string;
  typeKey?: string;
  countryCode?: string;
  descriptionKey?: string;
}

interface TwoColumnDragDropProps<T> {
  sourceItems: T[];
  totalData: T[];
  destinationItems: T[];
  onDragUpdate: (updatedSource: T[], updatedDest: T[]) => void;
  fieldMapping: FieldMapping;
  leftTitle?: string;
  rightTitle?: string;
  placeholderText?: string;
  layoutMode?: string;
  totalcount?: any;
}

const TwoColumnDragDrop = <T extends Record<string, any>>({
  totalData = [],
  sourceItems,
  destinationItems,
  totalcount,
  onDragUpdate,
  fieldMapping,
  layoutMode,
  leftTitle = "Available Items",
  rightTitle = "Selected Items",
  placeholderText = "Drag audit themes from the left panel and drop them here",
}: TwoColumnDragDropProps<T>) => {
  const [searchTerm, setSearchTerm] = useState("");

  const { idKey, nameKey, codeKey, imageKey, typeKey, descriptionKey, countryCode } =
    fieldMapping;
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());

  const filteredSourceItems = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return sourceItems;
    return sourceItems.filter((item) =>
      String(item[nameKey]).toLowerCase().includes(term)
    );
  }, [searchTerm, sourceItems, nameKey]);

  const onDragEnd = (result: DropResult) => {
    const { source, destination } = result;
    if (!destination) return;

    const moved =
      source.droppableId === "source"
        ? sourceItems[source.index]
        : destinationItems[source.index];

    if (source.droppableId !== destination.droppableId) {
      const newSource =
        source.droppableId === "source"
          ? sourceItems.filter((_, idx) => idx !== source.index)
          : [...sourceItems, moved];

      const newDest =
        source.droppableId === "source"
          ? [
            ...destinationItems.slice(0, destination.index),
            moved,
            ...destinationItems.slice(destination.index),
          ]
          : destinationItems.filter((_, idx) => idx !== source.index);

      onDragUpdate(newSource, newDest);

      // --- Add this: expand newly dragged item ---
      if (destination.droppableId === "destination") {
        setExpandedIds((prev) => new Set(prev).add(String(moved[idKey])));
      }

      return;
    }

    // Reordering inside the same list
    const list =
      source.droppableId === "source"
        ? [...sourceItems]
        : [...destinationItems];
    const [removed] = list.splice(source.index, 1);
    list.splice(destination.index, 0, removed);

    source.droppableId === "source"
      ? onDragUpdate(list, destinationItems)
      : onDragUpdate(sourceItems, list);
  };

  const handleDelete = (id: any) => {
    const removedItem = destinationItems.find((item) => item[idKey] === id);
    if (!removedItem) return;

    const newDest = destinationItems.filter((item) => item[idKey] !== id);
    const newSource = totalData?.filter(
      (item: any) =>
        !newDest.some((destItem) => destItem[idKey] === item[idKey])
    );

    onDragUpdate(newSource, newDest);
  };

  return (
    <div className={styles.dragDropContainer}>
      <DragDropContext onDragEnd={onDragEnd}>
        <div className={styles.columns}>
          {/* LEFT PANEL */}
          <div className={styles.leftPanel}>
            <div className={styles.panelHeader}>
              <h4>{leftTitle}</h4>
              <span className={styles.badge}>
                {totalcount}{" "}
                {layoutMode === "grid"
                  ? "Members"
                  : layoutMode === "accordion"
                    ? "Themes"
                    : ""}
              </span>
            </div>

            <div className={styles.orgcontent}>
              <div className={styles.searchBox}>
                <input
                  type="text"
                  placeholder={`Search ${leftTitle}`}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
                <FaSearch className={styles.searchIcon} />
              </div>
              <div className={styles.scrollauto}>
                <Droppable droppableId="source">
                  {(provided) => (
                    <div
                      className={styles.itemList}
                      ref={provided.innerRef}
                      {...provided.droppableProps}
                    >
                      {filteredSourceItems?.length === 0 ? (
                        <p className={styles.noResults}>No items found</p>
                      ) : (
                        filteredSourceItems?.map((item, index) => (
                          <Draggable
                            key={String(item[idKey])}
                            draggableId={String(item[idKey])}
                            index={index}
                          >
                            {(provided) => (
                              <div
                                className={styles.itemCard}
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                                {...provided.dragHandleProps}
                              >
                                {imageKey && item[imageKey] && (
                                  <img
                                    src={item[imageKey]}
                                    alt="thumb"
                                    className={styles.thumb}
                                  />
                                )}
                                <span>
                                  {typeKey && item?.countryCode && (
                                    <img
                                      src={getIconUrl(item.countryCode.toLowerCase())}
                                      alt={`${item.countryCode} flag`}
                                      className={styles.thumb}
                                      style={{
                                        width:
                                          item[nameKey] === "Cyber Security" || item[nameKey] === "IT Management"
                                            ? "35px"
                                            : "40px"
                                      }}

                                    />
                                  )}{" "}
                                  {item[nameKey]}{codeKey && item[codeKey] && ` (${item[codeKey]})`}
                                </span>
                                {typeKey && item?.[typeKey] === "policy" ? (
                                  <PolicyIcon className={styles.typeIcon} />
                                ) : typeKey && item?.[typeKey] === "custom" ? (
                                  <CustomIcon className={styles.typeIcon} />
                                ) : null}
                              </div>
                            )}
                          </Draggable>
                        ))
                      )}
                      {provided.placeholder}
                    </div>
                  )}
                </Droppable>
              </div>
            </div>
          </div>

          {/* RIGHT PANEL */}
          <div className={styles.rightPanel}>
            <div className={styles.panelHeader}>
              <h4>{rightTitle}</h4>
            </div>

            <div className={styles.orgcontentrigth}>
              <Droppable droppableId="destination">
                {(provided) => (
                  <div
                    className={styles.dropZone}
                    ref={provided.innerRef}
                    {...provided.droppableProps}
                  >
                    {destinationItems?.length === 0 ? (
                      <div className={styles.iconp}>
                        <Drage data-testid="drag-icon" />
                        <p className={styles.placeholderText}>
                          {placeholderText}
                        </p>
                      </div>
                    ) : (
                      <>
                        {layoutMode === "grid" && (
                          <div className={styles.gridLayout}>
                            {destinationItems?.map((item, index) => (
                              <Draggable
                                key={item[idKey] + "-" + index}
                                draggableId={item[idKey] + "-" + index}
                                index={index}
                              >
                                {(provided) => (
                                  <div
                                    className={styles.gridCard}
                                    ref={provided.innerRef}
                                    {...provided.draggableProps}
                                    {...provided.dragHandleProps}
                                  >
                                    <span className={styles.gridName}>
                                      {item[nameKey]}
                                    </span>
                                    <button
                                      onClick={() => handleDelete(item[idKey])}
                                      className={`${styles.actionButton} ${styles.remove}`}
                                    >
                                      <X size={18} />
                                    </button>
                                  </div>
                                )}
                              </Draggable>
                            ))}
                          </div>
                        )}

                        {layoutMode === "accordion" &&
                          destinationItems.map((item, index) => (
                            <Draggable
                              key={item[idKey] + "-" + index}
                              draggableId={item[idKey] + "-" + index}
                              index={index}
                            >
                              {(provided) => (
                                <div
                                  className={styles.accordionCard}
                                  ref={provided.innerRef}
                                  {...provided.draggableProps}
                                  {...provided.dragHandleProps}
                                >
                                  <div
                                    role="button"
                                    className={styles.accordionHeader}
                                    onClick={() => {
                                      const id = String(item[idKey]);
                                      const newSet = new Set(expandedIds);
                                      if (newSet.has(id)) {
                                        newSet.delete(id);
                                      } else {
                                        newSet.add(id);
                                      }
                                      setExpandedIds(newSet);
                                    }}
                                    onKeyDown={(e) => {
                                      if (e.key === "Enter" || e.key === " ") {
                                        e.preventDefault();
                                        const id = String(item[idKey]);
                                        const newSet = new Set(expandedIds);
                                        if (newSet.has(id)) {
                                          newSet.delete(id);
                                        } else {
                                          newSet.add(id);
                                        }
                                        setExpandedIds(newSet);
                                      }
                                    }}
                                    tabIndex={0}
                                  >
                                    <div
                                      className={styles.accordionTitleWrapper}
                                    >
                                      {/* Conditional icon */}
                                      {typeKey && item?.countryCode && (
                                        <img
                                          src={getIconUrl(item.countryCode.toLowerCase())}
                                          alt={`${item.countryCode} flag`}
                                          className={styles.thumb}
                                        />
                                      )}
                                      <span className={styles.accordionTitle}>
                                        {item[nameKey]}{codeKey && item[codeKey] && ` (${item[codeKey]})`}
                                      </span>
                                    </div>

                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleDelete(item[idKey]);
                                      }}
                                      className={`${styles.actionButton} ${styles.remove}`}
                                    >
                                      <X size={18} />
                                    </button>
                                  </div>

                                  {expandedIds.has(String(item[idKey])) &&
                                    descriptionKey &&
                                    item[descriptionKey]?.toString() && (
                                      <div className={styles.accordionBody}>
                                        <p>
                                          {item[descriptionKey]?.toString()}
                                        </p>
                                      </div>
                                    )}
                                </div>
                              )}
                            </Draggable>
                          ))}
                      </>
                    )}
                    {provided.placeholder}
                  </div>
                )}
              </Droppable>
            </div>
          </div>
        </div>
      </DragDropContext>
    </div>
  );
};

export default TwoColumnDragDrop;
